/**
 * Security Monitor
 * Tracks authentication-related security events and anomalies
 */

const SECURITY_LOG_KEY = 'security_events';
const MAX_LOG_SIZE = 100;

/**
 * Security event types
 */
export const SecurityEventType = {
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  TOKEN_REFRESH_SUCCESS: 'TOKEN_REFRESH_SUCCESS',
  TOKEN_REFRESH_FAILURE: 'TOKEN_REFRESH_FAILURE',
  TOKEN_DECRYPTION_FAILURE: 'TOKEN_DECRYPTION_FAILURE',
  UNAUTHORIZED_API_CALL: 'UNAUTHORIZED_API_CALL',
  SUSPICIOUS_ACTIVITY: 'SUSPICIOUS_ACTIVITY'
};

/**
 * Log security event
 * @param {string} eventType - Type of security event
 * @param {Object} metadata - Additional event metadata
 */
export const logSecurityEvent = (eventType, metadata = {}) => {
  const event = {
    type: eventType,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    ...metadata
  };

  try {
    const logs = getSecurityLogs();
    logs.unshift(event); // Add to beginning

    // Keep only recent logs
    const trimmedLogs = logs.slice(0, MAX_LOG_SIZE);

    localStorage.setItem(SECURITY_LOG_KEY, JSON.stringify(trimmedLogs));

    // Log to console in development
    if (import.meta.env.DEV) {
      console.log('[Security Event]', event);
    }

    // Check for suspicious patterns
    detectSuspiciousActivity(trimmedLogs);
  } catch (error) {
    console.error('Failed to log security event:', error);
  }
};

/**
 * Get security logs
 * @returns {Array} Array of security events
 */
export const getSecurityLogs = () => {
  try {
    const logs = localStorage.getItem(SECURITY_LOG_KEY);
    return logs ? JSON.parse(logs) : [];
  } catch (error) {
    console.error('Failed to retrieve security logs:', error);
    return [];
  }
};

/**
 * Clear security logs
 */
export const clearSecurityLogs = () => {
  localStorage.removeItem(SECURITY_LOG_KEY);
};

/**
 * Detect suspicious activity patterns
 * @param {Array} logs - Recent security logs
 */
const detectSuspiciousActivity = (logs) => {
  const recentLogs = logs.slice(0, 10); // Last 10 events
  const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);

  // Pattern 1: Multiple login failures in short time
  const recentFailures = recentLogs.filter(log =>
    log.type === SecurityEventType.LOGIN_FAILURE &&
    new Date(log.timestamp).getTime() > fiveMinutesAgo
  );

  if (recentFailures.length >= 3) {
    console.warn('[Security Alert] Multiple login failures detected:', recentFailures.length);
    logSecurityEvent(SecurityEventType.SUSPICIOUS_ACTIVITY, {
      pattern: 'MULTIPLE_LOGIN_FAILURES',
      count: recentFailures.length
    });
  }

  // Pattern 2: Multiple token refresh failures
  const recentRefreshFailures = recentLogs.filter(log =>
    log.type === SecurityEventType.TOKEN_REFRESH_FAILURE &&
    new Date(log.timestamp).getTime() > fiveMinutesAgo
  );

  if (recentRefreshFailures.length >= 3) {
    console.warn('[Security Alert] Multiple token refresh failures:', recentRefreshFailures.length);
    logSecurityEvent(SecurityEventType.SUSPICIOUS_ACTIVITY, {
      pattern: 'MULTIPLE_REFRESH_FAILURES',
      count: recentRefreshFailures.length
    });
  }

  // Pattern 3: Token decryption failure (possible tampering)
  const decryptionFailures = recentLogs.filter(log =>
    log.type === SecurityEventType.TOKEN_DECRYPTION_FAILURE
  );

  if (decryptionFailures.length > 0) {
    console.error('[Security Alert] Token decryption failure - possible tampering or device change');
  }
};

/**
 * Get security statistics
 * @returns {Object} Security statistics
 */
export const getSecurityStats = () => {
  const logs = getSecurityLogs();
  const oneHourAgo = Date.now() - (60 * 60 * 1000);
  const recentLogs = logs.filter(log =>
    new Date(log.timestamp).getTime() > oneHourAgo
  );

  return {
    totalEvents: logs.length,
    recentEvents: recentLogs.length,
    loginSuccesses: recentLogs.filter(l => l.type === SecurityEventType.LOGIN_SUCCESS).length,
    loginFailures: recentLogs.filter(l => l.type === SecurityEventType.LOGIN_FAILURE).length,
    tokenRefreshes: recentLogs.filter(l => l.type === SecurityEventType.TOKEN_REFRESH_SUCCESS).length,
    refreshFailures: recentLogs.filter(l => l.type === SecurityEventType.TOKEN_REFRESH_FAILURE).length,
    unauthorizedCalls: recentLogs.filter(l => l.type === SecurityEventType.UNAUTHORIZED_API_CALL).length,
    suspiciousActivities: recentLogs.filter(l => l.type === SecurityEventType.SUSPICIOUS_ACTIVITY).length
  };
};
